import React from 'react';
import PropTypes from 'prop-types';
import { connect } from '@gasbuddy/react';
import { DateRange } from 'react-date-range';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { Button } from '@gasbuddy/react-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import styles from './styles.css';
import sharedStyles from '../../styles/shared.css';
import { setDateRange } from '../../actions';
import bpUtils from '../../../lib/bputils';
import DateRangePropType from '../../utils/prop-types/date-range';

const { gTagEvent } = require('../../../lib/global-site-tag').default;

const presetRanges = [
  {
    label: 'Last Week',
    startDate: moment()
      .subtract(7, 'days')
      .startOf('week'),
    endDate: moment()
      .subtract(7, 'days')
      .endOf('week'),
  },
  {
    label: 'Last 30 days',
    startDate: moment().subtract(30, 'days'),
    endDate: moment(),
  },
  {
    label: 'Last 60 days',
    startDate: moment().subtract(60, 'days'),
    endDate: moment(),
  },
  {
    label: 'Last 90 days',
    startDate: moment().subtract(90, 'days'),
    endDate: moment(),
  },
];

function BpDateRangePicker({
  className,
  hideCustomDateRange,
  doNotDispatchChanges,
  dispatchSetDateRange,
  onChange,
  dateRange,
  gTagTitle,
  doNotStoreChanges,
}) {
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [disableApply, setDisableApply] = React.useState(false);
  const [label, setLabel] = React.useState('');
  const [minCalendarDate, setMinCalendarDate] = React.useState(undefined);
  const [maxCalendarDate, setMaxCalendarDate] = React.useState(moment());

  const dateRangeDropdown = React.useRef(null);
  const calendar = React.useRef(null);

  React.useEffect(() => {
    let newDateRange = {};

    window.addEventListener('mousedown', handleClickOutsideSelector);
    const dateRangeFromLocalStorage = bpUtils.store.get('selectedDateRange');
    const queryParams = bpUtils.getQueryParameters();
    if (queryParams?.startDate && queryParams?.endDate) {
      newDateRange = setDateRangeFromQueryString(queryParams.startDate, queryParams.endDate);
      storeDateRangeToLocalStorage(newDateRange.startDate, newDateRange.endDate, newDateRange.label);
      updateDateRange(newDateRange.startDate, newDateRange.endDate, newDateRange.label);
    } else if (dateRangeFromLocalStorage) {
      newDateRange = {
        startDate: moment(dateRangeFromLocalStorage.startDate),
        endDate: moment(dateRangeFromLocalStorage.endDate),
        label: dateRangeFromLocalStorage.label,
      };
      updateDateRange(newDateRange.startDate, newDateRange.endDate, newDateRange.label, false);
      storeDateRangeToQueryString(newDateRange.startDate, newDateRange.endDate);
    } else {
      // The Page is loaded for the first time after fresh-login i.e. there was no date in localstorage
      // eslint-disable-next-line prefer-destructuring
      newDateRange = presetRanges[1];
      updateDateRange(newDateRange.startDate, newDateRange.endDate, newDateRange.label, false);
      storeDateRange(newDateRange.startDate, newDateRange.endDate, newDateRange.label);
    }

    return () => {
      window.removeEventListener('mousedown', handleClickOutsideSelector);
    };
  }, []);

  React.useEffect(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      updateDateRange(dateRange.startDate, dateRange.endDate, dateRange.label, !doNotDispatchChanges);
    }
  }, [dateRange?.startDate, dateRange?.endDate]);

  const setDateRangeFromQueryString = (sDate, eDate) => {
    let newlabel = getLabelByDates(moment(sDate), moment(eDate));
    const presetRange = presetRanges.find(
      range => (range.startDate.format('YYYY-MM-DD') === sDate && range.endDate.format('YYYY-MM-DD') === eDate),
    );
    if (presetRange) {
      newlabel = presetRange.label;
    }
    const newDateRange = {
      label: newlabel,
      startDate: moment(sDate),
      endDate: moment(eDate),
    };
    updateDateRange(newDateRange.startDate, newDateRange.endDate, newDateRange.label, false);

    return newDateRange;
  };

  const getPresetDateRange = (newLabel) => {
    const newDateRange = presetRanges.find(range => range.label === newLabel);
    return {
      startDate: newDateRange.startDate,
      endDate: newDateRange.endDate,
    };
  };

  const getLabelByDates = (sDate, eDate) => {
    const presetRange = presetRanges.find(
      p => p.startDate.format('YYYY-MM-DD') === sDate.format('YYYY-MM-DD')
        && p.endDate.format('YYYY-MM-DD') === eDate.format('YYYY-MM-DD'),
    );

    if (presetRange) {
      return presetRange.label;
    }

    return formatDateRangeLabel(sDate, eDate);
  };

  const closeDropdown = () => setIsOpen(false);

  const sendDateChangeGTagEvent = (dateRangeLabel, sDate, eDate) => {
    const rangeType = presetRanges.find(range => range.label === dateRangeLabel) ? label : 'Custom Range';
    const eventLabel = { range_type: rangeType, start_date: sDate.format('YYYY-MM-DD'), end_date: eDate.format('YYYY-MM-DD') };
    gTagEvent(gTagTitle, `Selected Date Range - ${rangeType}`, eventLabel);
  };

  const handleRangeClick = (e) => {
    const optionLabel = e.currentTarget.getAttribute('data-selected-range');
    const newDateRange = getPresetDateRange(optionLabel);

    sendDateChangeGTagEvent(optionLabel, newDateRange.startDate, newDateRange.endDate);
    updateDateRange(newDateRange.startDate, newDateRange.endDate, optionLabel);

    setLabel(optionLabel);
    setIsOpen(false);
    setStartDate(newDateRange.startDate);
    setEndDate(newDateRange.endDate);
  };

  const handleApplyClick = () => {
    const { startDate: calendarStartDate, endDate: calendarEndDate } = calendar.current.state.range;
    const newLabel = getLabelByDates(calendarStartDate, calendarEndDate);

    sendDateChangeGTagEvent(newLabel, calendarStartDate, calendarEndDate);
    updateDateRange(calendarStartDate, calendarEndDate, newLabel);

    setIsOpen(false);
  };

  const handleOnChange = (newDateRange) => {
    const now = moment().endOf('day');
    if (newDateRange.startDate.format() === newDateRange.endDate.format()) {
      const oneYearInTheFuture = moment(newDateRange.startDate).add(1, 'year');
      const oneYearInThePast = moment(newDateRange.startDate).subtract(1, 'year');
      const newMaxDate = moment.min([now, oneYearInTheFuture]);
      setDisableApply(true);
      setMinCalendarDate(oneYearInThePast);
      setMaxCalendarDate(newMaxDate);
    } else {
      setDisableApply(false);
      setMinCalendarDate(undefined);
      setMaxCalendarDate(moment());
    }
  };

  const formatDateRangeLabel = (sDate, eDate) => {
    // if the startDate and endDate have diff years we need to the year of the startDate in the label
    const startDateFormat = `MMM Do ${sDate.format('YYYY') !== eDate.format('YYYY') ? 'YYYY' : ''}`;
    return `${sDate.format(startDateFormat)} - ${eDate.format('MMM Do YYYY')}`;
  };

  const storeDateRangeToQueryString = (sDate, eDate) => {
    if (!doNotStoreChanges) {
      bpUtils.setQueryParameter('startDate', sDate.format('YYYY-MM-DD'));
      bpUtils.setQueryParameter('endDate', eDate.format('YYYY-MM-DD'));
    }
  };

  const handleClickOutsideSelector = (event) => {
    if (dateRangeDropdown && !dateRangeDropdown?.current.getContainer().contains(event.target)) {
      closeDropdown();
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const storeDateRangeToLocalStorage = (sDate, eDate, newLabel) => {
    if (!doNotStoreChanges) {
      bpUtils.store.set('selectedDateRange', {
        label: newLabel,
        startDate: sDate.format('YYYY-MM-DD'),
        endDate: eDate.format('YYYY-MM-DD'),
      });
    }
  };

  const storeDateRange = (sDate, eDate, newLabel) => {
    storeDateRangeToLocalStorage(sDate, eDate, newLabel);
    storeDateRangeToQueryString(sDate, eDate);
  };

  const updateDateRange = (sDate, eDate, newLabel, shouldDispatch = !doNotDispatchChanges) => {
    storeDateRange(sDate, eDate, newLabel);
    setStartDate(sDate);
    setEndDate(eDate);
    setLabel(newLabel);

    const updatedDateRange = { startDate: sDate, endDate: eDate, label: newLabel };
    if (onChange) {
      onChange(updatedDateRange);
    }
    if (shouldDispatch) {
      dispatchSetDateRange(updatedDateRange);
    }
  };

  return (
    <div className={`${className} ${styles.dateRangeWrapper}`}>
      <Dropdown
        className={`${sharedStyles.rsDropdown} ${styles.rsDropdownOverride}`}
        isOpen={isOpen}
        toggle={handleToggle}
        ref={dateRangeDropdown}
      >
        <DropdownToggle
          caret
          className={sharedStyles.rsDropdownToggle}
        >
          <span>{label === 'Custom Range' ? formatDateRangeLabel(startDate, endDate) : label}</span>
          <FontAwesomeIcon className={sharedStyles.rsDropdownChevron} icon={isOpen ? faChevronUp : faChevronDown} />
        </DropdownToggle>
        <DropdownMenu className={sharedStyles.rsDropdownMenu}>
          {presetRanges.map(rangeOption => (
            <DropdownItem
              key={rangeOption.label}
              data-selected-range={rangeOption.label}
              onClick={handleRangeClick}
            >
              <span className={styles.selectedRangeLabel}>{rangeOption.label}</span>
            </DropdownItem>
          ))}
          <DropdownItem divider />
          <DropdownItem header>Custom Range</DropdownItem>
          {hideCustomDateRange ? null : (
            <div
              key="Custom Range"
              data-selected-range="Custom Range"
              style={{ height: '360px' }}
            >
              <div style={{ margin: '0 15px' }}>
                <div style={{ fontWeight: 700, fontSize: '16px', padding: '0 5px' }}>
                  Date Range
                </div>
                <DateRange
                  calendars={1}
                  onChange={handleOnChange}
                  ref={calendar}
                  startDate={startDate}
                  endDate={endDate}
                  maxDate={maxCalendarDate}
                  minDate={minCalendarDate}
                // theme={}
                />
                <Button
                  size="sm"
                  color="red"
                  onClick={handleApplyClick}
                  style={{ float: 'left' }}
                  disabled={disableApply}
                >
                  Apply
                </Button>
                <Button
                  size="sm"
                  color="red"
                  onClick={closeDropdown}
                  style={{ float: 'right' }}
                  bordered
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}

BpDateRangePicker.propTypes = {
  className: PropTypes.string,
  hideCustomDateRange: PropTypes.bool,
  doNotDispatchChanges: PropTypes.bool,
  dispatchSetDateRange: PropTypes.func,
  onChange: PropTypes.func,
  dateRange: DateRangePropType,
  gTagTitle: PropTypes.string,
  doNotStoreChanges: PropTypes.bool,
};

BpDateRangePicker.defaultProps = {
  className: '',
  hideCustomDateRange: false,
  doNotDispatchChanges: false,
  dispatchSetDateRange: null,
  onChange: null,
  dateRange: {},
  gTagTitle: null,
  doNotStoreChanges: false,
};

const mapStateToProps = state => ({
  dateRange: state.dateRange,
});

const mapDispatchToProps = dispatch => ({
  dispatchSetDateRange: newDateRange => dispatch(setDateRange(newDateRange)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BpDateRangePicker);
